import React, { useEffect, useState, useCallback } from 'react';
import { Badge } from 'reactstrap';
import { useStateSelector } from '../../store/selectors';
import './NotificationFlag.scss';
import { useDispatch } from 'react-redux';
import { addNotificationFlagToDelete } from '../../slices/notification-flags/notification-flags.slice';
import { flagCodeMatches } from '../../notifications/notification-flag-codes';

interface NotificationFlagProps {
    eventCode: string;
    reserveEmptySpace?: boolean;
    flagClassName?: string;
    includePrefix?: boolean;
    deletePrefixMatches?: boolean;
    isInitializing?: boolean;
    doNotRemove?: boolean;
    matchPattern?: (eventCode: string) => boolean;
}
export const NotificationFlag = (props: NotificationFlagProps) => {
    const notificationFlags = useStateSelector(
        (s) => s.notificationFlags.flags
    );
    const flagsToDelete = useStateSelector(
        (s) => s.notificationFlags.flagsToDelete
    );
    const dispatch = useDispatch();
    const [numberOfNotifications, setNumberNotifications] = useState(0);
    const [isHovered, setIsHovered] = useState(false);

    const countNumberOfNotifications = useCallback(() => {
        const flags = props?.matchPattern
            ? notificationFlags?.filter((f) => props.matchPattern!(f.eventCode))
            : notificationFlags?.filter((f) =>
                  flagCodeMatches(f.eventCode, props.eventCode, props?.includePrefix)
              );
        return flags?.reduce((sum, flag) => sum + flag.count, 0) || 0;
    }, [props.includePrefix, props.eventCode, props.matchPattern, notificationFlags]);

    const removeNotificationIfFullMatch = useCallback(() => {
        if (props.deletePrefixMatches) {
            notificationFlags
                .filter((f) => flagCodeMatches(f.eventCode, props.eventCode, true))
                .forEach((flag) => dispatch(addNotificationFlagToDelete(flag)));
        } else {
            const flag = notificationFlags.find((f) =>
                flagCodeMatches(f.eventCode, props.eventCode)
            );
            if (flag) {
                dispatch(addNotificationFlagToDelete(flag));
            }
        }
    }, [notificationFlags, props.eventCode, props.deletePrefixMatches, dispatch]);

    useEffect(() => {
        if (props?.eventCode || props?.matchPattern) {
            const numberOfNotifications = countNumberOfNotifications();
            setNumberNotifications(numberOfNotifications);

            const isAlreadyInDeleteList = flagsToDelete?.some((f) =>
                flagCodeMatches(f.eventCode, props.eventCode, props.deletePrefixMatches)
            );

            if (
                !props.matchPattern &&
                !isAlreadyInDeleteList &&
                !props.doNotRemove &&
                numberOfNotifications > 0
            ) {
                removeNotificationIfFullMatch();
            }
        }
    }, [
        props.eventCode,
        props.doNotRemove,
        props.matchPattern,
        flagsToDelete,
        countNumberOfNotifications,
        removeNotificationIfFullMatch,
    ]);

    return numberOfNotifications > 0 && !props.isInitializing ? (
        <>
            {props.reserveEmptySpace ? (
                <div className="notification-flag-placeholder"> </div>
            ) : (
                []
            )}
            <div
                className={
                    (props.flagClassName ? props.flagClassName : '') +
                    ' notification-flag'
                }
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}>
                <Badge
                    className={'notification-flag-badge'}
                    id={'notification-flag-' + props.eventCode}>
                    {isHovered
                        ? numberOfNotifications
                        : numberOfNotifications > 9
                        ? '9+'
                        : numberOfNotifications}
                </Badge>
            </div>
        </>
    ) : (
        <></>
    );
};
